import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Eye, EyeOff } from 'react-feather';
import logo from '../assets/images/ABN1.png';
import { encryptData, decryptData } from "../utils/crypto";

// --- Custom Animations & Styles ---
const CustomStyles = () => (
  <style>{`
    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }
    .animate-float {
      animation: float 3s ease-in-out infinite;
    }
    @keyframes blob {
      0% { transform: translate(0px, 0px) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    .animate-blob {
      animation: blob 7s infinite;
    }
    .animation-delay-2000 {
      animation-delay: 2s;
    }
    .animation-delay-4000 {
      animation-delay: 4s;
    }
    .glass-card {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.5);
    }
  `}</style>
);

const Login = ({ setIsAuthenticated }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const encryptedPayload = encryptData({ email, password });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/auth/admin-login`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ payload: encryptedPayload })
                }
            );

            const data = await response.json();

            if (!response.ok) throw new Error(data.message || "Login failed");
            if (!data.payload) throw new Error("Payload missing from server");

            const decrypted = decryptData(data.payload);

            if (decrypted.success && decrypted.token) {
                localStorage.setItem("authToken", decrypted.token);
                localStorage.setItem("user", JSON.stringify(decrypted.user));
                setIsAuthenticated(true);
                navigate("/");
            } else {
                throw new Error(decrypted.message || "Login failed");
            }
        } catch (err) {
            console.error("Login error:", err);
            setError(err.message || "Server error, please try again");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans relative overflow-hidden">
            <CustomStyles />

            {/* --- Animated Background Blobs --- */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
                {/* Blue Blob */}
                <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                {/* Purple Blob */}
                <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                {/* Sky Blob */}
                <div className="absolute -bottom-32 left-20 w-96 h-96 bg-sky-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            {/* --- Main Login Card --- */}
            <div className="glass-card rounded-[2rem] shadow-2xl p-8 sm:p-12 w-full max-w-md relative z-10 animate-fade-in-up">
                
                {/* Header */}
                <div className="mb-10 text-center">
                    <img src={logo} alt="Ahir Business Network" className="mx-auto mb-6 h-12 w-auto object-contain" />
                    <h2 className="mb-2 text-2xl font-extrabold tracking-tight text-slate-900">Admin sign in</h2>
                    <p className="font-medium text-slate-500">Manage chapters, members and meetings.</p>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm text-red-600 rounded-xl flex items-start gap-3 border border-red-100 animate-pulse shadow-sm">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm font-bold">{error}</p>
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                    
                    {/* Email Input */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                            Email Address
                        </label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Mail className="w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-300" />
                            </div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter Your Email"
                                required
                                className="w-full pl-12 pr-4 py-4 bg-white/60 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all duration-300 text-slate-800 placeholder-slate-400 font-medium shadow-sm hover:bg-white"
                            />
                        </div>
                    </div>

                    {/* Password Input */}
                    <div className="space-y-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                            Password
                        </label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Lock className="w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-300" />
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="w-full pl-12 pr-12 py-4 bg-white/60 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all duration-300 text-slate-800 placeholder-slate-400 font-medium shadow-sm hover:bg-white"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-blue-600 transition-colors cursor-pointer focus:outline-none"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-4 px-6 rounded-xl text-lg font-bold text-white shadow-lg shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-1 active:scale-[0.98] flex justify-center items-center gap-2 ${
                            isLoading 
                                ? 'bg-slate-400 cursor-not-allowed shadow-none' 
                                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Authenticating...</span>
                            </>
                        ) : (
                            <span>Sign In</span>
                        )}
                    </button>
                </form>
            </div>
            
            {/* Footer Text */}
            <div className="absolute bottom-6 text-center w-full z-10">
                <p className="text-sm font-medium text-slate-400">Ahir Business Network · Admin console</p>
            </div>
        </div>
    );
};

export default Login;